import type { QueryClient } from '@tanstack/react-query';
import {
	selectStockList,
	stockQueryOptions,
	trimsQueryOptions,
	vehicleQueryOptions,
} from '../api';
import { collapsedTwinIds } from '../common';
import { isConfigurable } from '../domain';
import { isValidPayload, isValidStockPayload } from './serialize';
import {
	SAVED_CONFIG_SCHEMA_VERSION,
	type SavedCarConfiguration,
	type UnavailableReason,
} from './types';

export type ApiClientLike = Parameters<typeof trimsQueryOptions>[0];

export type ProbeInput = {
	queryClient: QueryClient;
	apiClient: ApiClientLike;
	record: SavedCarConfiguration;
};

export async function probeAvailability({
	queryClient,
	apiClient,
	record,
}: ProbeInput): Promise<UnavailableReason | null> {
	if (!record.available && record.unavailableReason) {
		return record.unavailableReason;
	}

	if (record.version !== SAVED_CONFIG_SCHEMA_VERSION) return 'payload_invalid';

	if (record.vehicleType === 'stock') {
		if (!isValidStockPayload(record.payload)) return 'payload_invalid';

		const stock = await queryClient.fetchQuery(
			stockQueryOptions(
				apiClient,
				record.listing.makeCode,
				record.listing.modelCode
			)
		);
		const found = selectStockList(stock).vehicles.some(
			(v) => v.id === record.vehicleStockId
		);
		return found ? null : 'stock_sold';
	}

	if (!isValidPayload(record.payload)) return 'payload_invalid';

	const { progress } = record.payload;
	const ref = progress.vehicle!;

	const trims = await queryClient.fetchQuery(
		trimsQueryOptions(apiClient, progress.makeCode!, progress.modelCode!)
	);
	const trim = trims.find((t) => t.eurotaxCode === ref.eurotaxCode);

	if (!trim || !isConfigurable(trim)) return 'trim_delisted';

	let model;
	try {
		model = await queryClient.fetchQuery(vehicleQueryOptions(apiClient, ref));
	} catch {
		return 'listing_unavailable';
	}
	if (!model?.payload?.details) return 'listing_unavailable';

	const twins = collapsedTwinIds(model.catalog);
	const known = (id: number) => model!.catalog.byId.has(id) || twins.has(id);

	if (!progress.selectedOptions.every(known)) return 'options_invalid';

	if (
		!progress.choices.every((c) => known(c.accessoryId) && known(c.chosenId))
	) {
		return 'options_invalid';
	}

	return null;
}
